import { getImageInfo } from "components/jiffy/vue-product-details-print/utils/preload-image";

export default class ProductCardMadeCanvasClass {
  #url;

  static #previewsSelector = "[data-front-zoomed-image-url][data-printable-area]";

  constructor(props) {
    this.#url = props.url;
  }

  static get $previews() {
    return document.querySelectorAll(ProductCardMadeCanvasClass.#previewsSelector);
  }

  static get #previewsIds() {
    return Array.from(ProductCardMadeCanvasClass.$previews).map(preview => ({
      $preview: preview,
      area: {
        ...JSON.parse(preview.dataset.printableArea),
        image_url: preview.dataset.frontZoomedImageUrl
      }
    }));
  }

  static setDefaultPreviewSrc() {
    const $images = document.querySelectorAll(ProductCardMadeCanvasClass.#previewsSelector);
    $images.forEach($img => {
      const defaultSrc = $img.getAttribute("default-src");
      if (!defaultSrc) return;
      $img.setAttribute("src", defaultSrc);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async #addBackgroundCanvas(ctx, area) {
    const {
      image: backdropImage,
      width,
      height
    } = await getImageInfo(area.image_url, { crossOrigin: "anonymous" });

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.canvas.width = width;
    ctx.canvas.height = height;
    ctx.clearRect(0, 0, width, height); // clear canvas
    ctx.drawImage(backdropImage, 0, 0, width, height);
  }

  async #addArtworkCanvas(ctx, area) {
    const {
      image: artworkImage,
      width: widthPx,
      height: heightPx
    } = await getImageInfo(this.#url, {
      crossOrigin: "anonymous"
    });

    const { bounding_box: defaultBoundingBox, anchor, rotation_angle: rotationAngle } = area;
    const scale = 0.7; // Scale down to 70% to ensure it fits well within the bounding box
    const boundingBox = {
      width: defaultBoundingBox.width * scale,
      height: defaultBoundingBox.height * scale
    };

    const angle = (rotationAngle * Math.PI) / 180;
    ctx.save();
    ctx.translate(anchor.x, anchor.y);
    ctx.rotate(angle);
    let { width } = boundingBox;
    let height = (boundingBox.width / widthPx) * heightPx;

    if (height > boundingBox.height) {
      height = boundingBox.height;
      width = (boundingBox.height / heightPx) * widthPx;
    }

    ctx.drawImage(artworkImage, -width / 2, 0, width, height);
    ctx.restore();
  }

  // eslint-disable-next-line class-methods-use-this
  #addDebugCanvas(ctx, area) {
    const searchParams = new URLSearchParams(window.location.search);
    if (!searchParams.has("debug")) return;

    // Draw bounding box
    ctx.save();
    ctx.strokeStyle = "red";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      area.bounding_box.left,
      area.bounding_box.top,
      area.bounding_box.width,
      area.bounding_box.height
    );
    ctx.restore();

    // Draw anchor point
    ctx.save();
    ctx.fillStyle = "blue";
    ctx.beginPath();
    ctx.arc(area.anchor.x, area.anchor.y, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  async #initPreview({ $preview, area }) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    await this.#addBackgroundCanvas(ctx, area);
    this.#addDebugCanvas(ctx, area);
    await this.#addArtworkCanvas(ctx, area);
    const src = canvas.toDataURL();

    $preview.setAttribute("default-src", $preview.getAttribute("src"));
    $preview.setAttribute("src", src);
  }

  async init() {
    await Promise.all(ProductCardMadeCanvasClass.#previewsIds.map(this.#initPreview.bind(this)));
  }

  async initSingle($preview) {
    const previewData = ProductCardMadeCanvasClass.#previewsIds.find(
      ({ $preview: $p }) => $p === $preview
    );
    if (previewData) await this.#initPreview(previewData);
  }
}
